import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Users, CalendarCheck, GraduationCap, Star,
  Phone, Clock, ArrowRight, Plus, CheckCircle, AlertTriangle
} from 'lucide-react';
import { API_URL } from '@/config/api';

const TrainingDashboard = () => {
  const navigate = useNavigate();
  const { user, selectedBusiness } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDashboard = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/training/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) setData(await res.json());
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard, selectedBusiness?.business_id]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-4 border-pink-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = data?.stats || {};
  const today = data?.today || {};

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {getGreeting()}, {user?.name?.split(' ')[0] || 'there'}
        </h1>
        <p className="text-slate-500 mt-1">
          {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-pink-50 flex items-center justify-center">
                <Users className="w-5 h-5 text-pink-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.new_leads_this_week || 0}</p>
                <p className="text-xs text-slate-500">New students this week</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-50 flex items-center justify-center">
                <CalendarCheck className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.registrations_booked || 0}</p>
                <p className="text-xs text-slate-500">Registrations booked</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {stats.attendance_today?.present || 0}/{stats.attendance_today?.total || 0}
                </p>
                <p className="text-xs text-slate-500">Attendance today</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {stats.at_risk_attendance > 0 ? (
          <Card className="border-0 shadow-sm border-l-4 border-l-red-400 cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate('/courses')}>
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.at_risk_attendance}</p>
                  <p className="text-xs text-slate-500">At-risk attendance</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                  <Star className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.reviews_sent || 0}</p>
                  <p className="text-xs text-slate-500">Reviews sent</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Today's Priorities */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Callbacks Due */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Phone className="w-4 h-4 text-pink-500" />
              Callbacks Due
              {today.callbacks_due?.length > 0 && (
                <Badge className="bg-pink-100 text-pink-700 ml-auto">{today.callbacks_due.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!today.callbacks_due?.length ? (
              <p className="text-sm text-slate-400 py-4 text-center">No callbacks due today</p>
            ) : (
              <div className="space-y-3">
                {today.callbacks_due.slice(0, 5).map((cb, i) => (
                  <div key={cb.callback_id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium">{cb.lead_name}</p>
                      <p className="text-xs text-slate-500">{cb.reason}</p>
                    </div>
                    {cb.lead_phone && (
                      <a href={`tel:${cb.lead_phone}`} className="w-9 h-9 rounded-full bg-pink-100 text-pink-600 flex items-center justify-center hover:bg-pink-200 transition-colors">
                        <Phone className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Registrations Today */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CalendarCheck className="w-4 h-4 text-green-500" />
              Registrations Today
              {today.registrations_today?.length > 0 && (
                <Badge className="bg-green-100 text-green-700 ml-auto">{today.registrations_today.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!today.registrations_today?.length ? (
              <p className="text-sm text-slate-400 py-4 text-center">No registrations today</p>
            ) : (
              <div className="space-y-3">
                {today.registrations_today.slice(0, 5).map((reg, i) => (
                  <div key={reg.registration_id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium">{reg.lead_name}</p>
                      <p className="text-xs text-slate-500">{reg.registration_time || 'Time TBC'}</p>
                    </div>
                    <Badge className="bg-green-100 text-green-700 text-xs">{reg.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Classes Today */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-amber-500" />
              Classes Today
              {today.classes_today?.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700 ml-auto">{today.classes_today.length}</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!today.classes_today?.length ? (
              <p className="text-sm text-slate-400 py-4 text-center">No classes today</p>
            ) : (
              <div className="space-y-3">
                {today.classes_today.slice(0, 5).map((cls, i) => (
                  <div key={cls.course_id || i} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                    <div>
                      <p className="text-sm font-medium">{cls.name}</p>
                      <p className="text-xs text-slate-500">{cls.class_time_start} - {cls.class_time_end}</p>
                    </div>
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-white text-xs h-8 rounded-lg"
                      onClick={() => navigate(`/courses?id=${cls.course_id}&action=attendance`)}
                    >
                      Mark Register
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Button
          variant="outline"
          className="h-14 rounded-xl border-2 border-dashed border-slate-200 hover:border-pink-300 hover:bg-pink-50 transition-all flex items-center gap-2 text-sm"
          onClick={() => navigate('/leads?action=add-student')}
        >
          <Plus className="w-4 h-4" />
          Add Student
        </Button>
        <Button
          variant="outline"
          className="h-14 rounded-xl border-2 border-dashed border-slate-200 hover:border-pink-300 hover:bg-pink-50 transition-all flex items-center gap-2 text-sm"
          onClick={() => navigate('/manual-queue')}
        >
          <Phone className="w-4 h-4" />
          View Queue
        </Button>
        <Button
          variant="outline"
          className="h-14 rounded-xl border-2 border-dashed border-slate-200 hover:border-pink-300 hover:bg-pink-50 transition-all flex items-center gap-2 text-sm"
          onClick={() => navigate('/courses')}
        >
          <CheckCircle className="w-4 h-4" />
          Mark Attendance
        </Button>
        <Button
          variant="outline"
          className="h-14 rounded-xl border-2 border-dashed border-slate-200 hover:border-pink-300 hover:bg-pink-50 transition-all flex items-center gap-2 text-sm"
          onClick={() => navigate('/courses')}
        >
          <GraduationCap className="w-4 h-4" />
          View Courses
        </Button>
      </div>
    </div>
  );
};

export default TrainingDashboard;
